/** Inline CSS for email body preview iframes — keep in sync with normalizeComposeHtmlForSend(). */
export const EMAIL_BODY_PREVIEW_CSS = `
body{font-family:system-ui,-apple-system,sans-serif;font-size:14px;line-height:1.6;margin:16px;color:#1a1a1a;background:#fff;}
p{margin:0 0 12px 0;padding:0;}
p:last-child{margin-bottom:0;}
h1{margin:16px 0 8px 0;padding:0;font-size:24px;font-weight:700;line-height:1.3;}
h2{margin:14px 0 6px 0;padding:0;font-size:20px;font-weight:600;line-height:1.35;}
h3{margin:12px 0 4px 0;padding:0;font-size:18px;font-weight:600;line-height:1.4;}
ul,ol{margin:0 0 12px 0;padding-left:1.5em;}
li{margin:0 0 0.2em 0;}
blockquote{margin:0 0 12px 0;padding-left:0.75em;border-left:3px solid #d1d5db;color:#374151;}
table{border-collapse:collapse;width:100%;margin:0 0 12px 0;}
td,th{border:1px solid #e5e7eb;padding:6px 10px;text-align:left;vertical-align:top;}
th{font-weight:600;background:#f9fafb;}
a{color:#162a42;text-decoration:underline;text-underline-offset:2px;}
img{max-width:100%;height:auto;display:block;}
hr{border:none;border-top:1px solid #e5e7eb;margin:12px 0;}
`.replace(/\s+/g, ' ').trim();

export function composeImageInlineStyle(widthPercent: number): string {
  const w = Math.min(100, Math.max(15, widthPercent));
  return `width:${w}%;max-width:100%;height:auto;display:block;margin:0 0 12px 0`;
}

function paragraphInnerIsEmpty(inner: string): boolean {
  return inner
    .replace(/<br[^>]*>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/<[^>]+>/g, '')
    .trim() === '';
}

function cellBorderStyle(borderless: boolean): string {
  return borderless ? 'border:none;' : 'border:1px solid #e5e7eb;';
}

function headerBackgroundStyle(headerBg: string | null): string {
  if (headerBg === 'none') return 'background:transparent;';
  if (headerBg) return `background:${headerBg};`;
  return 'background:#f9fafb;';
}

function normalizeComposeTables(html: string): string {
  return html.replace(/<table\b([^>]*)>([\s\S]*?)<\/table>/gi, (_full, tableAttrs: string, inner: string) => {
    const borderless = /data-borderless="true"/.test(tableAttrs);
    const headerBgMatch = tableAttrs.match(/data-header-bg="([^"]*)"/);
    const headerBg = headerBgMatch?.[1] ?? null;
    const widthMatch = tableAttrs.match(/data-width-percent="(\d+)"/);
    const widthPercent = widthMatch ? widthMatch[1] : '100';

    const tableStyle = `width:${widthPercent}%;max-width:100%;margin:0 0 12px 0;border-collapse:collapse;`;
    let attrs = tableAttrs
      .replace(/\sstyle="[^"]*"/gi, '')
      .replace(/\sdata-width-percent="[^"]*"/gi, '');
    attrs += ` data-width-percent="${widthPercent}" style="${tableStyle}"`;

    const newInner = inner
      .replace(/<th\b([^>]*)>/gi, (_thFull, thAttrs: string) => {
        const style = [
          'padding:6px 10px;text-align:left;vertical-align:top;font-weight:600;',
          cellBorderStyle(borderless),
          headerBackgroundStyle(headerBg),
        ].join('');
        const cleaned = thAttrs.replace(/\sstyle="[^"]*"/gi, '');
        return `<th style="${style}"${cleaned}>`;
      })
      .replace(/<td\b([^>]*)>/gi, (_tdFull, tdAttrs: string) => {
        const style = [
          'padding:6px 10px;text-align:left;vertical-align:top;',
          cellBorderStyle(borderless),
        ].join('');
        const cleaned = tdAttrs.replace(/\sstyle="[^"]*"/gi, '');
        return `<td style="${style}"${cleaned}>`;
      });

    return `<table${attrs}>${newInner}</table>`;
  });
}

function normalizeComposeImages(html: string): string {
  return html.replace(/<img\b([^>]*data-upload-id="[^"]+"[^>]*)>/gi, (tag) => {
    const widthMatch = tag.match(/data-width-percent="(\d+)"/)
      ?? tag.match(/style="[^"]*width:\s*(\d+)%/);
    const widthPercent = widthMatch ? parseInt(widthMatch[1]!, 10) : 100;
    const style = composeImageInlineStyle(widthPercent);
    const withoutStyle = tag.replace(/\sstyle="[^"]*"/gi, '');
    return withoutStyle.replace('<img', `<img style="${style}"`);
  });
}

/**
 * Normalize compose HTML for outbound email and in-app preview parity.
 * - Paragraphs use explicit bottom margin (email clients ignore class-based CSS).
 * - Empty paragraphs become visible spacers (non-breaking space + margin).
 * - Headings get inline sizes/line-height tuned for sent mail, not editor defaults.
 * - Tables and images get inline layout styles for email clients.
 */
export function normalizeComposeHtmlForSend(html: string): string {
  let result = html;

  result = result.replace(/<p(\b[^>]*)?>([\s\S]*?)<\/p>/gi, (_match, _attrs, inner: string) => {
    if (paragraphInnerIsEmpty(inner)) {
      return '<p style="margin:0 0 12px 0;padding:0;line-height:14px;font-size:14px">&nbsp;</p>';
    }
    return `<p style="margin:0 0 12px 0;padding:0">${inner}</p>`;
  });

  result = result.replace(/<h1(\b[^>]*)?>/gi, '<h1 style="margin:16px 0 8px 0;padding:0;font-size:24px;font-weight:700;line-height:1.3">');
  result = result.replace(/<h2(\b[^>]*)?>/gi, '<h2 style="margin:14px 0 6px 0;padding:0;font-size:20px;font-weight:600;line-height:1.35">');
  result = result.replace(/<h3(\b[^>]*)?>/gi, '<h3 style="margin:12px 0 4px 0;padding:0;font-size:18px;font-weight:600;line-height:1.4">');

  result = normalizeComposeTables(result);
  result = normalizeComposeImages(result);

  return result;
}
